import { datadogRum } from '@datadog/browser-rum'
import isNil from 'lodash/isNil'
import { useTranslation } from 'react-i18next'
import { DocumentsHttpClient } from '@omnea-app/documents/documentsHttpClient'
import { onFetchFailErrors } from '@omnea-app/fe-api-client/fetchFailHandling'
import { supplierPortalPath } from '@omnea-app/fe-api-client/supplierPortalPath'
import { useToken } from '@omnea-app/fe-auth/TokenProvider'
import { useIsSupplierRole } from '@omnea-app/fe-auth/UserSessionProvider'
import { BlobUrlHandle } from '@omnea-app/fe-browser-utils/BlobUrlHandle'
import { isNotFoundApiError } from '@omnea-app/packages/universal/error-handling/isApiErrorOfType'
import { ApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { recoverFileExtension } from './recoverFileExtension'

const DocTypesViewableInBrowser = ['application/pdf', 'image/*']
const isDocTypeViewableInBrowser = (contentType: string) => {
  return (
    DocTypesViewableInBrowser.includes(contentType) ||
    DocTypesViewableInBrowser.some((type) => type.endsWith('/*') && contentType.startsWith(type.slice(0, -1)))
  )
}

export const useDownloadFiles = () => {
  const { getToken } = useToken()
  const isSupplier = useIsSupplierRole()
  const { t } = useTranslation('documents')

  const getBlobUrlHandle = async (
    url: string
  ): Promise<{ blobUrlHandle: BlobUrlHandle; isViewableInBrowser: boolean; contentType: string }> => {
    const response = await fetch(url)
    const contentType = response.headers.get('Content-Type') ?? ''
    const blob = await response.blob()
    const isViewableInBrowser = isDocTypeViewableInBrowser(contentType)

    return { blobUrlHandle: new BlobUrlHandle(blob), isViewableInBrowser, contentType }
  }

  const downloadDoc = async ({
    fileName,
    isViewableInBrowser,
    blobUrlHandle,
    forceDownload = false,
  }: {
    fileName: string
    isViewableInBrowser: boolean
    blobUrlHandle: BlobUrlHandle
    forceDownload?: boolean
  }) => {
    const link = document.createElement('a')
    if (isViewableInBrowser && !forceDownload) {
      if (!blobUrlHandle.url) return
      link.href = blobUrlHandle.url
      link.target = '_blank' // For preview
      link.rel = 'noopener noreferrer'
    } else {
      link.href = blobUrlHandle.url
    }

    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    blobUrlHandle.revoke()
  }

  const isForbiddenApiError = (error: unknown): error is { type: 'ForbiddenError'; detail: object } => {
    return !isNil(error) && typeof error === 'object' && 'type' in error && error.type === 'ForbiddenError'
  }

  const fetchSignedUrl = async (filestoreId: string, documentId?: string) => {
    try {
      const token = await getToken()
      const documentsClient = new DocumentsHttpClient({ authorization: token })
      return await documentsClient.GET(supplierPortalPath('/signed-url', isSupplier), {
        params: {
          query: {
            filestoreId,
            id: documentId,
          },
        },
      })
    } catch (error) {
      if (isNotFoundApiError(error)) {
        return {
          data: null,
          error: { type: 'NotFoundError' as const, detail: error.detail },
        }
      }
      throw error
    }
  }

  const getSignedUrl = async ({
    filestoreId,
    fileName,
    documentId,
  }: {
    filestoreId: string
    fileName: string
    documentId?: string
  }) => {
    try {
      const { data: documentDownloadResponse, error } = await fetchSignedUrl(filestoreId, documentId)

      if (error?.type === 'ForbiddenError') {
        throw new ApiError(error)
      }

      if (error?.type === 'NotFoundError') {
        return { notFound: true }
      }

      if (!documentDownloadResponse) {
        console.error('No response when downloading document', { error, filestoreId, fileName })
        return null
      }

      if (documentDownloadResponse.validationStatus === 'PENDING') {
        return null
      }

      return documentDownloadResponse.signedUrl
    } catch (error) {
      onFetchFailErrors('Downloading document failed', error, { filestoreId, fileName })
      // Re-throw ForbiddenError so polling hook can handle it
      if (error instanceof Error) {
        throw error
      }
      // Then check if it's a raw API error that needs wrapping
      if (isForbiddenApiError(error)) {
        throw new ApiError(error)
      }
      return null
    }
  }

  const downloadDocument = async ({
    filestoreId,
    fileName,
    mode,
    documentId,
  }: {
    filestoreId: string
    fileName: string
    mode?: 'preview' | 'downloadFile'
    documentId?: string
  }): Promise<
    | { type: 'preview'; url: string }
    | { type: 'download' }
    | { type: 'pending'; message: string }
    | { type: 'quarantined'; message: string }
    | { type: 'notFound'; message: string }
    | null
  > => {
    try {
      const { data: documentDownloadResponse, error } = await fetchSignedUrl(filestoreId, documentId)

      if (error?.type === 'ForbiddenError') {
        return {
          type: 'quarantined',
          message: t('file.unavailable'),
        }
      }

      if (error?.type === 'NotFoundError') {
        return {
          type: 'notFound',
          message: t('file.not-found'),
        }
      }

      if (!documentDownloadResponse) {
        console.error('No response when downloading document', { error, filestoreId, fileName })
        return null
      }

      if (documentDownloadResponse.validationStatus === 'PENDING') {
        return {
          type: 'pending',
          message: documentDownloadResponse.message,
        }
      }

      const s3SignedUrl = documentDownloadResponse.signedUrl

      const { blobUrlHandle, isViewableInBrowser, contentType } = await getBlobUrlHandle(s3SignedUrl)
      if (mode !== 'preview' || !isViewableInBrowser) {
        const { fileName: downloadFileName, hadKnownExtension } = recoverFileExtension({ fileName, contentType })
        if (!hadKnownExtension) {
          datadogRum.addAction('documentDownloadExtensionRecovery', {
            contentType,
            recovered: downloadFileName !== fileName,
          })
        }
        await downloadDoc({
          fileName: downloadFileName,
          isViewableInBrowser,
          blobUrlHandle,
          forceDownload: mode === 'downloadFile',
        })
        return { type: 'download' }
      } else {
        return { type: 'preview', url: s3SignedUrl }
      }
    } catch (error) {
      onFetchFailErrors('Downloading document failed', error, { filestoreId, fileName })
      return null
    }
  }

  return { getSignedUrl, downloadDocument }
}
